import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  User,
  UserCreateRequest,
  UserUpdateRequest,
  Restaurant,
  TableInfo,
  TableStatusOption,
  TableStatusDef,
  TableStatusDefInput,
  PosTerminal,
  MenuItem,
  MenuItemApiResponse,
  UpdateMenuItemPayload,
  ManagedMenuCategory,
  ManagedMenuGroup,
  MenuImportResult,
  DashboardStats,
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  Permission,
  RestaurantGroup,
  RestaurantGroupCreateRequest,
  RestaurantGroupUpdateRequest,
  RestaurantInGroup,
  SyncOutboxMessage,
  SyncInboxMessage,
  SyncConflict,
  RestaurantSyncStatus,
  ResolveConflictRequest,
  AIExtractionJob,
  AIImportOptions,
  AIImportResult,
  PosImportResult,
  Subscription,
  BillingUsage,
  Plan,
  Invoice,
  PaymentMethod,
  BillingDetails,
  AuditLogEntry,
  AuditAction,
  AuditCategory,
  AuditVerificationResult,
  TerminalErrorEntry,
  PosErrorLevel,
  InventoryItem,
  StockMovement,
  InventoryAlert,
  InventoryCategory,
  ReportMetrics,
  InventoryReport,
  ItemsReport,
  OperationsReport,
  ReportRequest,
  RevenueTrendPoint,
  SalesDetailReport,
  SalesSummaryReport,
  TaxReport,
  DiscountReport,
  PaymentsReport,
  MenuBreakdownReport,
  FoodDrinkReport,
  AlcoholFoodDrinkReport,
  DeliveryChargeReport,
  ExceptionsReport,
  SalesAreaReport,
  ShiftInfo,
  FloorInfo,
  TableZoneInfo,
  ManagedTableInfo,
  FloorLayoutInfo,
  BookingRuleInfo,
  BookingSectionInfo,
  BookingScheduleInfo,
  SpecialDayInfo,
  SyncedMenuCategory,
  SyncedMenuGroup,
  SyncedMenuItem,
  SyncedTax,
  SyncedDiscount,
  DiscountOptions,
  SyncedOrderType,
  SyncedCustomPayment,
  SyncedCookingInstruction,
  SyncedKitchenDisplayOrder,
  SyncedPrinterGroup,
  SyncedAccountSalesArea,
  SyncedAskNextReason,
  SyncedLanguage,
  SyncedShift,
  SyncedBookingInfo,
  BookingMutationPayload,
  SyncedTableInfo,
  MeResponse,
  SyncDraftArea,
  SyncDraftStatus,
} from '@/types/api';

class ApiClient {
  private client: AxiosInstance;
  private refreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      // Same-origin deployment: Spring Boot serves the dashboard static files
      // alongside /api, so a relative base URL resolves correctly in both dev
      // (Vite proxies /api → localhost:8080) and prod (single origin).
      baseURL: '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add auth token (skip for auth endpoints)
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const isAuthEndpoint = config.url?.includes('/auth/login') ||
                               config.url?.includes('/auth/refresh');
        const token = localStorage.getItem('accessToken');
        if (token && config.headers && !isAuthEndpoint) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // Don't try to refresh token for auth endpoints - just propagate the error
        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/refresh');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          if (this.refreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.refreshing = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              throw new Error('No refresh token');
            }

            const response = await axios.post<LoginResponse>(
              '/api/v1/auth/refresh',
              { refreshToken }
            );

            const { token } = response.data;
            localStorage.setItem('accessToken', token);

            // H1 fix: sync the zustand store's accessToken so persisted state
            // doesn't hold a stale token across page refreshes. localStorage is
            // the single source of truth for the request interceptor, but the
            // store field must stay in sync for any component that reads it.
            // H3 fix: trigger a WS reconnect so the STOMP layer authenticates
            // with the fresh token instead of the now-stale one it captured at
            // connect time.
            // Dynamic imports avoid circular-dependency concerns (authStore
            // depends on apiClient).
            import('../stores/authStore').then(({ useAuthStore }) => {
              useAuthStore.setState({ accessToken: token });
            });
            import('./websocket').then(({ websocketService }) => {
              websocketService.forceReconnect();
            });

            // Refresh the session-shaped user view in the background (don't block the retried request).
            this.getMe()
              .then((me) =>
                import('../stores/authStore').then(({ useAuthStore }) =>
                  useAuthStore.getState().setSession(me)
                )
              )
              .catch((meErr) => {
                console.warn('Failed to refresh /me after token refresh', meErr);
              });

            this.refreshSubscribers.forEach((cb) => cb(token));
            this.refreshSubscribers = [];

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');

            // Also reset the Zustand-persisted auth-storage blob, not just the flat
            // localStorage mirror above — otherwise it still holds isAuthenticated:
            // true with cached permissions, so PublicOnlyRoute immediately bounces
            // the post-redirect /login page back to "/", which refetches protected
            // data with the same stale token and re-triggers this exact failure,
            // reloading forever.
            import('../stores/authStore').then(({ useAuthStore }) => {
              useAuthStore.setState({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                permissions: new Set<string>(),
                scope: null,
              });
            });

            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            this.refreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth API
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(
      '/v1/auth/login',
      credentials
    );
    return response.data;
  }

  async logout(): Promise<void> {
    // The backend only revokes the token passed in the body; without it the refresh
    // token stays valid server-side until natural expiry (L3 fix).
    const refreshToken = localStorage.getItem('refreshToken');
    await this.client.post('/v1/auth/logout', refreshToken ? { refreshToken } : undefined);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async getMe(): Promise<MeResponse> {
    const response = await this.client.get<MeResponse>('/v1/me');
    return response.data;
  }

  // Restaurant API
  async getRestaurants(
    page = 0,
    size = 20,
    filters?: { search?: string; active?: boolean; state?: string }
  ): Promise<PaginatedResponse<Restaurant>> {
    const response = await this.client.get<PaginatedResponse<Restaurant>>(
      '/v1/restaurants',
      {
        params: {
          page,
          size,
          search: filters?.search || undefined,
          active: filters?.active,
          state: filters?.state || undefined,
        },
      }
    );
    return response.data;
  }

  async getRestaurantStates(): Promise<string[]> {
    const response = await this.client.get<string[]>('/v1/restaurants/states');
    return response.data;
  }

  async getRestaurant(id: number): Promise<Restaurant> {
    const response = await this.client.get<Restaurant>(`/v1/restaurants/${id}`);
    return response.data;
  }

  async createRestaurant(data: Partial<Restaurant>): Promise<Restaurant> {
    const response = await this.client.post<Restaurant>('/v1/restaurants', data);
    return response.data;
  }

  async updateRestaurant(
    id: number,
    data: Partial<Restaurant>
  ): Promise<Restaurant> {
    const response = await this.client.put<Restaurant>(
      `/v1/restaurants/${id}`,
      data
    );
    return response.data;
  }

  async deleteRestaurant(id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${id}`);
  }

  // Table API (backend controller not yet implemented)
  async getTables(restaurantId: number): Promise<TableInfo[]> {
    const response = await this.client.get<TableInfo[]>(
      `/v1/restaurants/${restaurantId}/tables`
    );
    return response.data;
  }

  async getTableStatuses(restaurantId: number): Promise<TableStatusOption[]> {
    const response = await this.client.get<TableStatusOption[]>(
      `/v1/restaurants/${restaurantId}/table-statuses`
    );
    return response.data;
  }

  async setTableStatus(restaurantId: number, tableId: number, posStatusId: string): Promise<void> {
    await this.client.patch(
      `/v1/restaurants/${restaurantId}/tables/${tableId}/table-status`,
      { posStatusId }
    );
  }

  // --- TableStatus definition management (Floor section) ---
  async getTableStatusDefs(restaurantId: number): Promise<TableStatusDef[]> {
    const response = await this.client.get<TableStatusDef[]>(
      `/v1/restaurants/${restaurantId}/table-statuses/all`
    );
    return response.data;
  }

  async createTableStatusDef(restaurantId: number, body: TableStatusDefInput): Promise<TableStatusDef> {
    const response = await this.client.post<TableStatusDef>(
      `/v1/restaurants/${restaurantId}/table-statuses`,
      body
    );
    return response.data;
  }

  async updateTableStatusDef(
    restaurantId: number,
    uuid: string,
    body: TableStatusDefInput
  ): Promise<TableStatusDef> {
    const response = await this.client.put<TableStatusDef>(
      `/v1/restaurants/${restaurantId}/table-statuses/${uuid}`,
      body
    );
    return response.data;
  }

  async deleteTableStatusDef(restaurantId: number, uuid: string): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/table-statuses/${uuid}`);
  }

  // Coarse status (AVAILABLE/OCCUPIED/RESERVED) — broadcast to POS as flag changes.
  async updateTableStatus(
    restaurantId: number,
    tableId: number,
    status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED'
  ): Promise<TableInfo> {
    const response = await this.client.patch<TableInfo>(
      `/v1/restaurants/${restaurantId}/tables/${tableId}/status`,
      { status }
    );
    return response.data;
  }

  async getTable(restaurantId: number, tableId: number): Promise<TableInfo> {
    const response = await this.client.get<TableInfo>(
      `/v1/restaurants/${restaurantId}/tables/${tableId}`
    );
    return response.data;
  }

  async createTable(
    restaurantId: number,
    data: Partial<TableInfo>
  ): Promise<TableInfo> {
    const response = await this.client.post<TableInfo>(
      `/v1/restaurants/${restaurantId}/tables`,
      data
    );
    return response.data;
  }

  async updateTable(
    restaurantId: number,
    tableId: number,
    data: Partial<TableInfo>
  ): Promise<TableInfo> {
    const response = await this.client.put<TableInfo>(
      `/v1/restaurants/${restaurantId}/tables/${tableId}`,
      data
    );
    return response.data;
  }

  async deleteTable(restaurantId: number, tableId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/tables/${tableId}`);
  }

  // POS Terminal API
  async getTerminals(restaurantId: number): Promise<PosTerminal[]> {
    const response = await this.client.get<PosTerminal[]>(
      `/v1/pos-terminals/restaurant/${restaurantId}`
    );
    return response.data;
  }

  async getTerminal(terminalId: number): Promise<PosTerminal> {
    const response = await this.client.get<PosTerminal>(
      `/v1/pos-terminals/${terminalId}`
    );
    return response.data;
  }

  async registerTerminal(
    restaurantId: number,
    data: Partial<PosTerminal>
  ): Promise<PosTerminal> {
    const response = await this.client.post<PosTerminal>(
      '/v1/pos-terminals',
      { ...data, restaurantId }
    );
    return response.data;
  }

  async updateTerminal(
    terminalId: number,
    data: Partial<PosTerminal>
  ): Promise<PosTerminal> {
    const response = await this.client.put<PosTerminal>(
      `/v1/pos-terminals/${terminalId}`,
      data
    );
    return response.data;
  }

  async deleteTerminal(terminalId: number): Promise<void> {
    await this.client.delete(`/v1/pos-terminals/${terminalId}`);
  }

  // Menu API

  // MenuItemApiResponse -> MenuItem: backend calls the visibility flag `visible`,
  // frontend calls it `available` - this is the single place that translates between them.
  private toMenuItem(restaurantId: number, item: MenuItemApiResponse): MenuItem {
    return {
      ...item,
      restaurantId,
      category: item.categoryName ?? 'Uncategorized',
      menuGroup: item.groupName ?? item.categoryName ?? 'Uncategorized',
      groupId: item.groupId,
      categoryId: item.categoryId,
      available: item.visible ?? true,
    };
  }

  async getMenuItems(restaurantId: number): Promise<MenuItem[]> {
    // Use managed menu items - these have correct group_id references
    // matching managed_menu_groups.id (synced items use POS group IDs which don't match)
    const response = await this.client.get<
      MenuItemApiResponse[] | { content: MenuItemApiResponse[] }
    >(`/v1/restaurants/${restaurantId}/menu/items`, { params: { size: 1000 } });
    const data = response.data;
    const items = Array.isArray(data) ? data : (data.content ?? []);
    return items.map((item) => this.toMenuItem(restaurantId, item));
  }

  async getMenuItem(
    restaurantId: number,
    itemId: number
  ): Promise<MenuItem> {
    const response = await this.client.get<MenuItem>(
      `/v1/restaurants/${restaurantId}/menu/items/${itemId}`
    );
    return response.data;
  }

  async createMenuItem(
    restaurantId: number,
    data: Partial<MenuItem> & { groupId: number }
  ): Promise<MenuItem> {
    const groupId = data.groupId;
    const response = await this.client.post<MenuItem>(
      `/v1/restaurants/${restaurantId}/menu/groups/${groupId}/items`,
      data
    );
    return response.data;
  }

  async updateMenuItem(
    restaurantId: number,
    itemId: number,
    data: UpdateMenuItemPayload
  ): Promise<MenuItem> {
    const response = await this.client.put<MenuItem>(
      `/v1/restaurants/${restaurantId}/menu/items/${itemId}`,
      data
    );
    return response.data;
  }

  async deleteMenuItem(restaurantId: number, itemId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/menu/items/${itemId}`);
  }

  // User API
  async getUsers(includeInactive = false): Promise<User[]> {
    const response = await this.client.get<User[]>('/v1/users', {
      params: { includeInactive },
    });
    return response.data;
  }

  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await this.client.post<User>('/v1/users', data);
    return response.data;
  }

  async updateUser(id: number, data: UserUpdateRequest): Promise<User> {
    const response = await this.client.put<User>(`/v1/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.client.delete(`/v1/users/${id}`);
  }

  async checkUsernameExists(username: string): Promise<boolean> {
    const response = await this.client.get<boolean>(
      `/v1/users/username/${encodeURIComponent(username)}/exists`
    );
    return response.data;
  }

  async checkEmailExists(email: string): Promise<boolean> {
    const response = await this.client.get<boolean>(
      `/v1/users/email/${encodeURIComponent(email)}/exists`
    );
    return response.data;
  }

  // Role API
  async getRoles(includeInactive = false): Promise<Role[]> {
    const response = await this.client.get<Role[]>('/v1/roles', {
      params: { includeInactive },
    });
    return response.data;
  }

  async createRole(data: RoleCreateRequest): Promise<Role> {
    const response = await this.client.post<Role>('/v1/roles', data);
    return response.data;
  }

  async updateRole(id: number, data: RoleUpdateRequest): Promise<Role> {
    const response = await this.client.put<Role>(`/v1/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: number): Promise<void> {
    await this.client.delete(`/v1/roles/${id}`);
  }

  async checkRoleNameExists(name: string): Promise<boolean> {
    const response = await this.client.get<boolean>(
      `/v1/roles/name/${encodeURIComponent(name)}/exists`
    );
    return response.data;
  }

  // Permission API
  async getPermissions(): Promise<Permission[]> {
    const response = await this.client.get<Permission[]>('/v1/permissions');
    return response.data;
  }

  // Dashboard API
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<DashboardStats>('/v1/dashboard/stats');
    return response.data;
  }

  // Health Check
  async healthCheck(): Promise<{ status: string }> {
    const response = await this.client.get<{ status: string }>(
      '/actuator/health'
    );
    return response.data;
  }

  // Restaurant Group API
  async getGroups(): Promise<RestaurantGroup[]> {
    const response = await this.client.get<RestaurantGroup[]>('/v1/restaurant-groups');
    return response.data;
  }

  async getGroup(id: number): Promise<RestaurantGroup> {
    const response = await this.client.get<RestaurantGroup>(`/v1/restaurant-groups/${id}`);
    return response.data;
  }

  async createGroup(data: RestaurantGroupCreateRequest): Promise<RestaurantGroup> {
    const response = await this.client.post<RestaurantGroup>('/v1/restaurant-groups', data);
    return response.data;
  }

  async updateGroup(id: number, data: RestaurantGroupUpdateRequest): Promise<RestaurantGroup> {
    const response = await this.client.put<RestaurantGroup>(`/v1/restaurant-groups/${id}`, data);
    return response.data;
  }

  async deleteGroup(id: number): Promise<void> {
    await this.client.delete(`/v1/restaurant-groups/${id}`);
  }

  async checkGroupNameExists(name: string): Promise<boolean> {
    const response = await this.client.get<boolean>(
      `/v1/restaurant-groups/name/${encodeURIComponent(name)}/exists`
    );
    return response.data;
  }

  async getGroupRestaurants(groupId: number): Promise<RestaurantInGroup[]> {
    const response = await this.client.get<RestaurantInGroup[]>(
      `/v1/restaurant-groups/${groupId}/restaurants`
    );
    return response.data;
  }

  async getGroupUsers(groupId: number): Promise<User[]> {
    const response = await this.client.get<User[]>(`/v1/restaurant-groups/${groupId}/users`);
    return response.data;
  }

  // Sync API
  // Outbox/Inbox use the flat SyncMonitoringController (paginated, requires restaurantId).
  // Conflicts use the per-restaurant SyncController (already implemented with auth).
  // DLQ uses SyncDlqController under /v1/sync/dlq (sync.manage authority).
  async getSyncOutbox(
    restaurantId: number,
    status?: string,
    page = 0,
    size = 50
  ): Promise<PaginatedResponse<SyncOutboxMessage>> {
    const response = await this.client.get<PaginatedResponse<SyncOutboxMessage>>(
      '/v1/sync/outbox',
      { params: { restaurantId, status, page, size } }
    );
    return response.data;
  }

  async getSyncInbox(
    restaurantId: number,
    status?: string,
    page = 0,
    size = 50
  ): Promise<PaginatedResponse<SyncInboxMessage>> {
    const response = await this.client.get<PaginatedResponse<SyncInboxMessage>>(
      '/v1/sync/inbox',
      { params: { restaurantId, status, page, size } }
    );
    return response.data;
  }

  async getSyncDlq(
    restaurantId: number,
    page = 0,
    size = 50
  ): Promise<PaginatedResponse<SyncInboxMessage>> {
    const response = await this.client.get<PaginatedResponse<SyncInboxMessage>>(
      '/v1/sync/dlq',
      { params: { restaurantId, page, size } }
    );
    return response.data;
  }

  async retrySyncMessage(messageId: string): Promise<void> {
    await this.client.post(`/v1/sync/dlq/${messageId}/retry`);
  }

  async discardSyncMessage(messageId: string): Promise<void> {
    await this.client.delete(`/v1/sync/dlq/${messageId}`);
  }

  // Outbox messages live in a different table than DLQ (inbox) messages and are keyed
  // by their numeric id, not the syncId UUID -- retrySyncMessage/discardSyncMessage
  // above only ever operate on sync_inbox rows and 404 on an outbox id (C1).
  async retryOutboxMessage(restaurantId: number, outboxId: number): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/sync/outbox/${outboxId}/retry`);
  }

  async cancelOutboxMessage(restaurantId: number, outboxId: number): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/sync/outbox/${outboxId}/cancel`);
  }

  async getSyncStatus(restaurantId: number): Promise<RestaurantSyncStatus> {
    const response = await this.client.get<RestaurantSyncStatus>(
      `/v1/restaurants/${restaurantId}/sync/status`
    );
    return response.data;
  }

  async getSyncConflicts(restaurantId: number): Promise<SyncConflict[]> {
    const response = await this.client.get<SyncConflict[]>(
      `/v1/restaurants/${restaurantId}/sync/conflicts`
    );
    return response.data;
  }

  async resolveConflict(
    restaurantId: number,
    conflictId: number,
    body: ResolveConflictRequest
  ): Promise<SyncConflict> {
    const response = await this.client.post<SyncConflict>(
      `/v1/restaurants/${restaurantId}/sync/conflicts/${conflictId}/resolve`,
      body
    );
    return response.data;
  }

  async forceSyncRestaurant(restaurantId: number): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/sync/trigger`);
  }

  // Ask the POS to push its full current state to the server (server becomes a copy of the POS).
  // Returns 202 — the data arrives asynchronously and is ingested by the inbound sync pipeline.
  async requestFullSyncFromPos(restaurantId: number): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/sync/request-full-sync`);
  }

  // AI Menu Extraction API
  async startAIExtraction(
    restaurantId: number,
    files: File[],
    options?: { targetCategory?: string; validFrom?: string; validTo?: string }
  ): Promise<AIExtractionJob> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (options?.targetCategory) {
      formData.append('targetCategory', options.targetCategory);
    }
    if (options?.validFrom) {
      formData.append('validFrom', options.validFrom);
    }
    if (options?.validTo) {
      formData.append('validTo', options.validTo);
    }

    const response = await this.client.post<AIExtractionJob>(
      `/v1/restaurants/${restaurantId}/menu/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes for upload
      }
    );
    return response.data;
  }

  async getAIExtractionJob(restaurantId: number, jobId: string): Promise<AIExtractionJob> {
    const response = await this.client.get<AIExtractionJob>(
      `/v1/restaurants/${restaurantId}/menu/upload/jobs/${jobId}`
    );
    return response.data;
  }

  async importAIExtractedItems(
    restaurantId: number,
    jobId: string,
    itemIds: string[],
    options: AIImportOptions
  ): Promise<AIImportResult> {
    const response = await this.client.post<AIImportResult>(
      `/v1/restaurants/${restaurantId}/menu/upload/jobs/${jobId}/import`,
      { itemIds, ...options }
    );
    return response.data;
  }

  async getMenuCategories(restaurantId: number): Promise<string[]> {
    const response = await this.client.get<string[]>(
      `/v1/restaurants/${restaurantId}/menu/categories`
    );
    return response.data;
  }

  // Synced Entities API
  async getSyncedMenuCategories(restaurantId: number): Promise<SyncedMenuCategory[]> {
    const { data } = await this.client.get<SyncedMenuCategory[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-categories`
    );
    return data;
  }

  async getSyncedMenuGroups(restaurantId: number): Promise<SyncedMenuGroup[]> {
    const { data } = await this.client.get<SyncedMenuGroup[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-groups`
    );
    return data;
  }

  async getSyncedMenuItems(restaurantId: number): Promise<SyncedMenuItem[]> {
    const { data } = await this.client.get<SyncedMenuItem[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-items`
    );
    return data;
  }

  async getSyncedTaxes(restaurantId: number): Promise<SyncedTax[]> {
    const { data } = await this.client.get<SyncedTax[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/taxes`
    );
    return data;
  }

  async getSyncedDiscounts(restaurantId: number): Promise<SyncedDiscount[]> {
    const { data } = await this.client.get<SyncedDiscount[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/discounts`
    );
    return data;
  }

  async getSyncedOrderTypes(restaurantId: number): Promise<SyncedOrderType[]> {
    const { data } = await this.client.get<SyncedOrderType[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/order-types`
    );
    return data;
  }

  async getSyncedCustomPayments(restaurantId: number): Promise<SyncedCustomPayment[]> {
    const { data } = await this.client.get<SyncedCustomPayment[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/custom-payments`
    );
    return data;
  }

  async getSyncedCookingInstructions(restaurantId: number): Promise<SyncedCookingInstruction[]> {
    const { data } = await this.client.get<SyncedCookingInstruction[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/cooking-instructions`
    );
    return data;
  }

  async getSyncedKitchenDisplayOrders(restaurantId: number): Promise<SyncedKitchenDisplayOrder[]> {
    const { data } = await this.client.get<SyncedKitchenDisplayOrder[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/kitchen-display-orders`
    );
    return data;
  }

  async getSyncedPrinterGroups(restaurantId: number): Promise<SyncedPrinterGroup[]> {
    const { data } = await this.client.get<SyncedPrinterGroup[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/printer-groups`
    );
    return data;
  }

  async getSyncedAccountSalesAreas(restaurantId: number): Promise<SyncedAccountSalesArea[]> {
    const { data } = await this.client.get<SyncedAccountSalesArea[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/account-sales-areas`
    );
    return data;
  }

  async getSyncedAskNextReasons(restaurantId: number): Promise<SyncedAskNextReason[]> {
    const { data } = await this.client.get<SyncedAskNextReason[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/ask-next-reasons`
    );
    return data;
  }

  async getSyncedLanguages(restaurantId: number): Promise<SyncedLanguage[]> {
    const { data } = await this.client.get<SyncedLanguage[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/languages`
    );
    return data;
  }

  async getSyncedShifts(restaurantId: number): Promise<SyncedShift[]> {
    const { data } = await this.client.get<SyncedShift[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/shifts`
    );
    return data;
  }

  async getSyncedBookings(
    restaurantId: number,
    params?: { startDate?: string; endDate?: string; date?: string; status?: string; section?: string }
  ): Promise<SyncedBookingInfo[]> {
    const { data } = await this.client.get<SyncedBookingInfo[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/bookings`,
      { params }
    );
    return data;
  }

  async createSyncedBooking(restaurantId: number, payload: BookingMutationPayload): Promise<SyncedBookingInfo> {
    const { data } = await this.client.post<SyncedBookingInfo>(
      `/v1/restaurants/${restaurantId}/synced-entities/bookings`, payload
    );
    return data;
  }

  /**
   * Update a booking. The canonical lookup key is the booking's uuid — unique
   * across both managed_bookings (cloud-created) and synced_bookings
   * (POS-originated). When the uuid resolves only to a synced row, the server
   * creates a managed shadow on the fly so subsequent edits round-trip
   * normally.
   */
  async updateSyncedBooking(restaurantId: number, uuid: string, payload: BookingMutationPayload): Promise<SyncedBookingInfo> {
    const { data } = await this.client.put<SyncedBookingInfo>(
      `/v1/restaurants/${restaurantId}/synced-entities/bookings/by-uuid/${encodeURIComponent(uuid)}`, payload
    );
    return data;
  }

  async getSyncedTables(restaurantId: number): Promise<SyncedTableInfo[]> {
    const { data } = await this.client.get<SyncedTableInfo[]>(
      `/v1/restaurants/${restaurantId}/synced-entities/tables`
    );
    return data;
  }

  // Synced Entity CRUD (Tax)
  async createSyncedTax(restaurantId: number, data: Partial<SyncedTax>): Promise<SyncedTax> {
    const { data: result } = await this.client.post<SyncedTax>(
      `/v1/restaurants/${restaurantId}/synced-entities/taxes`, data
    );
    return result;
  }

  async updateSyncedTax(restaurantId: number, id: number, data: Partial<SyncedTax>): Promise<SyncedTax> {
    const { data: result } = await this.client.put<SyncedTax>(
      `/v1/restaurants/${restaurantId}/synced-entities/taxes/${id}`, data
    );
    return result;
  }

  async deleteSyncedTax(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/taxes/${id}`);
  }

  // Synced Entity CRUD (Language)
  async createSyncedLanguage(restaurantId: number, data: Partial<SyncedLanguage>): Promise<SyncedLanguage> {
    const { data: result } = await this.client.post<SyncedLanguage>(
      `/v1/restaurants/${restaurantId}/synced-entities/languages`, data
    );
    return result;
  }

  async updateSyncedLanguage(restaurantId: number, id: number, data: Partial<SyncedLanguage>): Promise<SyncedLanguage> {
    const { data: result } = await this.client.put<SyncedLanguage>(
      `/v1/restaurants/${restaurantId}/synced-entities/languages/${id}`, data
    );
    return result;
  }

  async deleteSyncedLanguage(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/languages/${id}`);
  }

  // Synced Entity CRUD (Menu Category)
  async createSyncedMenuCategory(restaurantId: number, data: Partial<SyncedMenuCategory>): Promise<SyncedMenuCategory> {
    const { data: result } = await this.client.post<SyncedMenuCategory>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-categories`, data
    );
    return result;
  }

  async updateSyncedMenuCategory(restaurantId: number, id: number, data: Partial<SyncedMenuCategory>): Promise<SyncedMenuCategory> {
    const { data: result } = await this.client.put<SyncedMenuCategory>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-categories/${id}`, data
    );
    return result;
  }

  async deleteSyncedMenuCategory(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/menu-categories/${id}`);
  }

  // Synced Entity CRUD (Cooking Instruction)
  async createSyncedCookingInstruction(restaurantId: number, data: Partial<SyncedCookingInstruction>): Promise<SyncedCookingInstruction> {
    const { data: result } = await this.client.post<SyncedCookingInstruction>(
      `/v1/restaurants/${restaurantId}/synced-entities/cooking-instructions`, data
    );
    return result;
  }

  async updateSyncedCookingInstruction(restaurantId: number, id: number, data: Partial<SyncedCookingInstruction>): Promise<SyncedCookingInstruction> {
    const { data: result } = await this.client.put<SyncedCookingInstruction>(
      `/v1/restaurants/${restaurantId}/synced-entities/cooking-instructions/${id}`, data
    );
    return result;
  }

  async deleteSyncedCookingInstruction(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/cooking-instructions/${id}`);
  }

  // Synced Entity CRUD (Menu Group)
  async createSyncedMenuGroup(restaurantId: number, data: Partial<SyncedMenuGroup>): Promise<SyncedMenuGroup> {
    const { data: result } = await this.client.post<SyncedMenuGroup>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-groups`, data
    );
    return result;
  }

  async updateSyncedMenuGroup(restaurantId: number, id: number, data: Partial<SyncedMenuGroup>): Promise<SyncedMenuGroup> {
    const { data: result } = await this.client.put<SyncedMenuGroup>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-groups/${id}`, data
    );
    return result;
  }

  async deleteSyncedMenuGroup(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/menu-groups/${id}`);
  }

  // Synced Entity CRUD (Menu Item)
  async createSyncedMenuItem(restaurantId: number, data: Partial<SyncedMenuItem>): Promise<SyncedMenuItem> {
    const { data: result } = await this.client.post<SyncedMenuItem>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-items`, data
    );
    return result;
  }

  async updateSyncedMenuItem(restaurantId: number, id: number, data: Partial<SyncedMenuItem>): Promise<SyncedMenuItem> {
    const { data: result } = await this.client.put<SyncedMenuItem>(
      `/v1/restaurants/${restaurantId}/synced-entities/menu-items/${id}`, data
    );
    return result;
  }

  async deleteSyncedMenuItem(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/menu-items/${id}`);
  }

  // Synced Entity CRUD (Custom Payment)
  async createSyncedCustomPayment(restaurantId: number, data: Partial<SyncedCustomPayment>): Promise<SyncedCustomPayment> {
    const { data: result } = await this.client.post<SyncedCustomPayment>(
      `/v1/restaurants/${restaurantId}/synced-entities/custom-payments`, data
    );
    return result;
  }

  async updateSyncedCustomPayment(restaurantId: number, id: number, data: Partial<SyncedCustomPayment>): Promise<SyncedCustomPayment> {
    const { data: result } = await this.client.put<SyncedCustomPayment>(
      `/v1/restaurants/${restaurantId}/synced-entities/custom-payments/${id}`, data
    );
    return result;
  }

  async deleteSyncedCustomPayment(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/custom-payments/${id}`);
  }

  // Synced Entity CRUD (Kitchen Display Order)
  async createSyncedKitchenDisplayOrder(restaurantId: number, data: Partial<SyncedKitchenDisplayOrder>): Promise<SyncedKitchenDisplayOrder> {
    const { data: result } = await this.client.post<SyncedKitchenDisplayOrder>(
      `/v1/restaurants/${restaurantId}/synced-entities/kitchen-display-orders`, data
    );
    return result;
  }

  async updateSyncedKitchenDisplayOrder(restaurantId: number, id: number, data: Partial<SyncedKitchenDisplayOrder>): Promise<SyncedKitchenDisplayOrder> {
    const { data: result } = await this.client.put<SyncedKitchenDisplayOrder>(
      `/v1/restaurants/${restaurantId}/synced-entities/kitchen-display-orders/${id}`, data
    );
    return result;
  }

  async deleteSyncedKitchenDisplayOrder(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/kitchen-display-orders/${id}`);
  }

  // Synced Entity CRUD (Account Sales Area)
  async createSyncedAccountSalesArea(restaurantId: number, data: Partial<SyncedAccountSalesArea>): Promise<SyncedAccountSalesArea> {
    const { data: result } = await this.client.post<SyncedAccountSalesArea>(
      `/v1/restaurants/${restaurantId}/synced-entities/account-sales-areas`, data
    );
    return result;
  }

  async updateSyncedAccountSalesArea(restaurantId: number, id: number, data: Partial<SyncedAccountSalesArea>): Promise<SyncedAccountSalesArea> {
    const { data: result } = await this.client.put<SyncedAccountSalesArea>(
      `/v1/restaurants/${restaurantId}/synced-entities/account-sales-areas/${id}`, data
    );
    return result;
  }

  async deleteSyncedAccountSalesArea(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/account-sales-areas/${id}`);
  }

  // Synced Entity CRUD (Ask Next Reason)
  async createSyncedAskNextReason(restaurantId: number, data: Partial<SyncedAskNextReason>): Promise<SyncedAskNextReason> {
    const { data: result } = await this.client.post<SyncedAskNextReason>(
      `/v1/restaurants/${restaurantId}/synced-entities/ask-next-reasons`, data
    );
    return result;
  }

  async updateSyncedAskNextReason(restaurantId: number, id: number, data: Partial<SyncedAskNextReason>): Promise<SyncedAskNextReason> {
    const { data: result } = await this.client.put<SyncedAskNextReason>(
      `/v1/restaurants/${restaurantId}/synced-entities/ask-next-reasons/${id}`, data
    );
    return result;
  }

  async deleteSyncedAskNextReason(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/ask-next-reasons/${id}`);
  }

  // Synced Entity CRUD (Discount)
  async createSyncedDiscount(restaurantId: number, data: Partial<SyncedDiscount>): Promise<SyncedDiscount> {
    const { data: result } = await this.client.post<SyncedDiscount>(
      `/v1/restaurants/${restaurantId}/synced-entities/discounts`, data
    );
    return result;
  }

  async updateSyncedDiscount(restaurantId: number, id: number, data: Partial<SyncedDiscount>): Promise<SyncedDiscount> {
    const { data: result } = await this.client.put<SyncedDiscount>(
      `/v1/restaurants/${restaurantId}/synced-entities/discounts/${id}`, data
    );
    return result;
  }

  async deleteSyncedDiscount(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/discounts/${id}`);
  }

  async getDiscountOptions(restaurantId: number): Promise<DiscountOptions> {
    const { data } = await this.client.get<DiscountOptions>(
      `/v1/restaurants/${restaurantId}/synced-entities/discounts/options`
    );
    return data;
  }

  // Synced Entity CRUD (Order Type)
  async createSyncedOrderType(restaurantId: number, data: Partial<SyncedOrderType>): Promise<SyncedOrderType> {
    const { data: result } = await this.client.post<SyncedOrderType>(
      `/v1/restaurants/${restaurantId}/synced-entities/order-types`, data
    );
    return result;
  }

  async updateSyncedOrderType(restaurantId: number, id: number, data: Partial<SyncedOrderType>): Promise<SyncedOrderType> {
    const { data: result } = await this.client.put<SyncedOrderType>(
      `/v1/restaurants/${restaurantId}/synced-entities/order-types/${id}`, data
    );
    return result;
  }

  async deleteSyncedOrderType(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/order-types/${id}`);
  }

  // Synced Entity CRUD (Printer Group)
  async createSyncedPrinterGroup(restaurantId: number, data: Partial<SyncedPrinterGroup>): Promise<SyncedPrinterGroup> {
    const { data: result } = await this.client.post<SyncedPrinterGroup>(
      `/v1/restaurants/${restaurantId}/synced-entities/printer-groups`, data
    );
    return result;
  }

  async updateSyncedPrinterGroup(restaurantId: number, id: number, data: Partial<SyncedPrinterGroup>): Promise<SyncedPrinterGroup> {
    const { data: result } = await this.client.put<SyncedPrinterGroup>(
      `/v1/restaurants/${restaurantId}/synced-entities/printer-groups/${id}`, data
    );
    return result;
  }

  async deleteSyncedPrinterGroup(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/printer-groups/${id}`);
  }

  // Synced Entity CRUD (Shifts)
  async createSyncedShift(restaurantId: number, data: Partial<SyncedShift>): Promise<SyncedShift> {
    const { data: result } = await this.client.post<SyncedShift>(
      `/v1/restaurants/${restaurantId}/synced-entities/shifts`, data
    );
    return result;
  }

  async updateSyncedShift(restaurantId: number, id: number, data: Partial<SyncedShift>): Promise<SyncedShift> {
    const { data: result } = await this.client.put<SyncedShift>(
      `/v1/restaurants/${restaurantId}/synced-entities/shifts/${id}`, data
    );
    return result;
  }

  async deleteSyncedShift(restaurantId: number, id: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/synced-entities/shifts/${id}`);
  }

  // Managed Menu API (Category → Group → Item hierarchy)
  async getManagedCategories(restaurantId: number): Promise<ManagedMenuCategory[]> {
    const response = await this.client.get<ManagedMenuCategory[]>(
      `/v1/restaurants/${restaurantId}/menu/categories`
    );
    return response.data;
  }

  async getManagedGroups(restaurantId: number, categoryId: number): Promise<ManagedMenuGroup[]> {
    const response = await this.client.get<ManagedMenuGroup[]>(
      `/v1/restaurants/${restaurantId}/menu/categories/${categoryId}/groups`
    );
    return response.data;
  }

  async getGroupItems(restaurantId: number, groupId: number): Promise<MenuItem[]> {
    const response = await this.client.get<MenuItemApiResponse[]>(
      `/v1/restaurants/${restaurantId}/menu/groups/${groupId}/items`
    );
    return response.data.map((item) => this.toMenuItem(restaurantId, item));
  }

  async importMenuFromPos(restaurantId: number): Promise<MenuImportResult> {
    const response = await this.client.post<MenuImportResult>(
      `/v1/restaurants/${restaurantId}/menu/import-from-pos`
    );
    return response.data;
  }

  async importAllFromPos(restaurantId: number): Promise<PosImportResult> {
    const response = await this.client.post<PosImportResult>(
      `/v1/restaurants/${restaurantId}/sync/import-from-pos`
    );
    return response.data;
  }

  // Billing API (backend controllers not yet implemented)
  async getSubscription(): Promise<Subscription> {
    const response = await this.client.get<Subscription>('/v1/billing/subscription');
    return response.data;
  }

  async getBillingUsage(): Promise<BillingUsage> {
    const response = await this.client.get<BillingUsage>('/v1/billing/usage');
    return response.data;
  }

  async getPlans(): Promise<Plan[]> {
    const response = await this.client.get<Plan[]>('/v1/billing/plans');
    return response.data;
  }

  async changePlan(planId: string): Promise<Subscription> {
    const response = await this.client.post<Subscription>('/v1/billing/subscription/change', {
      planId,
    });
    return response.data;
  }

  async getInvoices(page = 0, size = 20): Promise<PaginatedResponse<Invoice>> {
    const response = await this.client.get<PaginatedResponse<Invoice>>('/v1/billing/invoices', {
      params: { page, size },
    });
    return response.data;
  }

  async downloadInvoice(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v1/billing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await this.client.get<PaymentMethod[]>('/v1/billing/payment-methods');
    return response.data;
  }

  async addPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
    const response = await this.client.post<PaymentMethod>('/v1/billing/payment-methods', {
      paymentMethodId,
    });
    return response.data;
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await this.client.post(`/v1/billing/payment-methods/${paymentMethodId}/default`);
  }

  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    await this.client.delete(`/v1/billing/payment-methods/${paymentMethodId}`);
  }

  async setupMandate(request: { restaurantGroupId: number; email: string }): Promise<{ billingRequestFlowUrl: string }> {
    const response = await this.client.post<{ billingRequestFlowUrl: string }>(
      '/v1/billing/setup-mandate',
      request
    );
    return response.data;
  }

  async getBillingDetails(): Promise<BillingDetails> {
    const response = await this.client.get<BillingDetails>('/v1/billing/details');
    return response.data;
  }

  async updateBillingDetails(details: Partial<BillingDetails>): Promise<BillingDetails> {
    const response = await this.client.put<BillingDetails>('/v1/billing/details', details);
    return response.data;
  }

  // Audit Log API
  async getAuditLogs(params: {
    userId?: number;
    category?: AuditCategory;
    action?: AuditAction;
    entityType?: string;
    entityId?: string;
    tenantId?: string;
    restaurantId?: number;
    groupId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedResponse<AuditLogEntry>> {
    const response = await this.client.get<PaginatedResponse<AuditLogEntry>>('/v1/audit', {
      params,
    });
    return response.data;
  }

  async getAuditEntry(eventId: string): Promise<AuditLogEntry> {
    const response = await this.client.get<AuditLogEntry>(`/v1/audit/${eventId}`);
    return response.data;
  }

  async getEntityHistory(entityType: string, entityId: string): Promise<AuditLogEntry[]> {
    const response = await this.client.get<AuditLogEntry[]>(
      `/v1/audit/entity/${entityType}/${entityId}/history`
    );
    return response.data;
  }

  async getUserActivity(
    userId: number,
    page = 0,
    size = 50
  ): Promise<PaginatedResponse<AuditLogEntry>> {
    const response = await this.client.get<PaginatedResponse<AuditLogEntry>>(
      `/v1/audit/user/${userId}/activity`,
      { params: { page, size } }
    );
    return response.data;
  }

  async verifyAuditIntegrity(
    startDate: string,
    endDate: string
  ): Promise<AuditVerificationResult> {
    const response = await this.client.get<AuditVerificationResult>('/v1/audit/verify', {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async exportGdprData(userId: number, userEmail?: string): Promise<AuditLogEntry[]> {
    const response = await this.client.get<AuditLogEntry[]>(`/v1/audit/gdpr/export/${userId}`, {
      params: { userEmail },
    });
    return response.data;
  }

  // POS Terminal Error API
  async getTerminalErrors(params: {
    restaurantId?: number;
    groupId?: number;
    terminalId?: string;
    errorClass?: string;
    level?: PosErrorLevel;
    fromTs?: string;
    toTs?: string;
    resolved?: boolean;
    page?: number;
    size?: number;
  }): Promise<PaginatedResponse<TerminalErrorEntry>> {
    const response = await this.client.get<PaginatedResponse<TerminalErrorEntry>>(
      '/v1/terminal-errors',
      { params }
    );
    return response.data;
  }

  async getTerminalError(id: number): Promise<TerminalErrorEntry> {
    const response = await this.client.get<TerminalErrorEntry>(`/v1/terminal-errors/${id}`);
    return response.data;
  }

  async markTerminalErrorResolved(id: number): Promise<void> {
    await this.client.patch(`/v1/terminal-errors/${id}/resolve`);
  }

  // Inventory API
  async getInventoryItems(restaurantId: number, params?: {
    category?: string;
    lowStock?: boolean;
    search?: string;
    page?: number;
    size?: number;
  }): Promise<{ content: InventoryItem[]; totalElements: number; totalPages: number; number: number }> {
    const response = await this.client.get<{ content: InventoryItem[]; totalElements: number; totalPages: number; number: number }>(
      `/v1/restaurants/${restaurantId}/inventory`,
      { params }
    );
    return response.data;
  }

  async getInventoryItem(restaurantId: number, itemId: number): Promise<InventoryItem> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/inventory/${itemId}`);
    return response.data;
  }

  async createInventoryItem(restaurantId: number, item: Partial<InventoryItem>): Promise<InventoryItem> {
    const response = await this.client.post(`/v1/restaurants/${restaurantId}/inventory`, item);
    return response.data;
  }

  async updateInventoryItem(restaurantId: number, itemId: number, item: Partial<InventoryItem>): Promise<InventoryItem> {
    const response = await this.client.put(`/v1/restaurants/${restaurantId}/inventory/${itemId}`, item);
    return response.data;
  }

  async deleteInventoryItem(restaurantId: number, itemId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/inventory/${itemId}`);
  }

  async adjustInventoryStock(restaurantId: number, itemId: number, adjustment: {
    quantity: number;
    type: 'IN' | 'OUT' | 'ADJUSTMENT';
    reason: string;
    reference?: string;
  }): Promise<StockMovement> {
    const response = await this.client.post(`/v1/restaurants/${restaurantId}/inventory/${itemId}/adjust`, adjustment);
    return response.data;
  }

  async getStockMovements(restaurantId: number, itemId: number): Promise<StockMovement[]> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/inventory/${itemId}/movements`);
    return response.data;
  }

  async getLowStockAlerts(restaurantId: number): Promise<InventoryAlert[]> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/inventory/alerts`);
    return response.data;
  }

  async getInventoryCategories(restaurantId: number): Promise<InventoryCategory[]> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/inventory/categories`);
    return response.data;
  }

  // Reports API
  async getRevenueReport(restaurantId: number, startDate: string, endDate: string): Promise<ReportMetrics> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/reports/revenue`, {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async getInventoryReport(restaurantId: number): Promise<InventoryReport> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/reports/inventory`);
    return response.data;
  }

  async getItemsReport(restaurantId: number, startDate: string, endDate: string): Promise<ItemsReport> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/reports/items`, {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async getOperationsReport(restaurantId: number, startDate: string, endDate: string): Promise<OperationsReport> {
    const response = await this.client.get(`/v1/restaurants/${restaurantId}/reports/operations`, {
      params: { startDate, endDate },
    });
    return response.data;
  }

  async getRevenueTrend(
    restaurantId: number,
    startDate: string,
    endDate: string
  ): Promise<RevenueTrendPoint[]> {
    const response = await this.client.get<RevenueTrendPoint[]>(
      `/v1/restaurants/${restaurantId}/reports/revenue/trend`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getSalesDetail(restaurantId: number, startDate: string, endDate: string): Promise<SalesDetailReport> {
    const response = await this.client.get<SalesDetailReport>(
      `/v1/restaurants/${restaurantId}/reports/sales-detail`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getSalesSummary(restaurantId: number, startDate: string, endDate: string): Promise<SalesSummaryReport> {
    const response = await this.client.get<SalesSummaryReport>(
      `/v1/restaurants/${restaurantId}/reports/sales-summary`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getTaxReport(restaurantId: number, startDate: string, endDate: string): Promise<TaxReport> {
    const response = await this.client.get<TaxReport>(
      `/v1/restaurants/${restaurantId}/reports/tax`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getDiscountReport(restaurantId: number, startDate: string, endDate: string): Promise<DiscountReport> {
    const response = await this.client.get<DiscountReport>(
      `/v1/restaurants/${restaurantId}/reports/discount`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getPaymentsReport(restaurantId: number, startDate: string, endDate: string): Promise<PaymentsReport> {
    const response = await this.client.get<PaymentsReport>(
      `/v1/restaurants/${restaurantId}/reports/payments`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getMenuBreakdown(restaurantId: number, startDate: string, endDate: string): Promise<MenuBreakdownReport> {
    const response = await this.client.get<MenuBreakdownReport>(
      `/v1/restaurants/${restaurantId}/reports/menu-breakdown`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getSalesAreaBreakdown(
    restaurantId: number,
    startDate: string,
    endDate: string,
    startTime?: string,
    endTime?: string
  ): Promise<SalesAreaReport> {
    const params: Record<string, string> = { startDate, endDate };
    if (startTime && endTime) {
      params.startTime = startTime;
      params.endTime = endTime;
    }
    const response = await this.client.get<SalesAreaReport>(
      `/v1/restaurants/${restaurantId}/reports/sales-area`,
      { params }
    );
    return response.data;
  }

  async getFoodDrinkReport(restaurantId: number, startDate: string, endDate: string): Promise<FoodDrinkReport> {
    const response = await this.client.get<FoodDrinkReport>(
      `/v1/restaurants/${restaurantId}/reports/food-drink`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getAlcoholFoodDrinkReport(restaurantId: number, startDate: string, endDate: string): Promise<AlcoholFoodDrinkReport> {
    const response = await this.client.get<AlcoholFoodDrinkReport>(
      `/v1/restaurants/${restaurantId}/reports/alcohol-food-drink`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getDeliveryChargeReport(restaurantId: number, startDate: string, endDate: string): Promise<DeliveryChargeReport> {
    const response = await this.client.get<DeliveryChargeReport>(
      `/v1/restaurants/${restaurantId}/reports/delivery-charge`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async getExceptionsReport(restaurantId: number, startDate: string, endDate: string): Promise<ExceptionsReport> {
    const response = await this.client.get<ExceptionsReport>(
      `/v1/restaurants/${restaurantId}/reports/exceptions`,
      { params: { startDate, endDate } }
    );
    return response.data;
  }

  async exportReport(restaurantId: number, request: ReportRequest, format: 'PDF' | 'CSV'): Promise<Blob> {
    const response = await this.client.post(
      `/v1/restaurants/${restaurantId}/reports/export`,
      { ...request, format },
      { responseType: 'blob' }
    );
    return response.data;
  }

  // ==================== Shift API ====================

  async getShifts(restaurantId: number): Promise<ShiftInfo[]> {
    const response = await this.client.get<ShiftInfo[]>(`/v1/restaurants/${restaurantId}/shifts`);
    return response.data;
  }

  async createShift(restaurantId: number, data: Partial<ShiftInfo>): Promise<ShiftInfo> {
    const response = await this.client.post<ShiftInfo>(`/v1/restaurants/${restaurantId}/shifts`, data);
    return response.data;
  }

  async updateShift(restaurantId: number, shiftId: number, data: Partial<ShiftInfo>): Promise<ShiftInfo> {
    const response = await this.client.put<ShiftInfo>(`/v1/restaurants/${restaurantId}/shifts/${shiftId}`, data);
    return response.data;
  }

  async deleteShift(restaurantId: number, shiftId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/shifts/${shiftId}`);
  }

  // ==================== Draft Mode API ====================

  async getDraftStatus(restaurantId: number, area: SyncDraftArea): Promise<SyncDraftStatus> {
    const response = await this.client.get<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}`
    );
    return response.data;
  }

  async enableDraftMode(restaurantId: number, area: SyncDraftArea): Promise<SyncDraftStatus> {
    const response = await this.client.post<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}/enable`
    );
    return response.data;
  }

  /** Publishes the area's staged changes to the POS and leaves draft mode. */
  async pushDraftChanges(restaurantId: number, area: SyncDraftArea): Promise<SyncDraftStatus> {
    const response = await this.client.post<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}/push`
    );
    return response.data;
  }

  /** Discards the staged changes, restoring the area from the POS, and leaves draft mode. */
  async cancelDraftChanges(restaurantId: number, area: SyncDraftArea): Promise<SyncDraftStatus> {
    const response = await this.client.post<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}/cancel`
    );
    return response.data;
  }

  /** Schedules the area's staged changes to be pushed automatically at `pushAt` (UTC ISO instant). */
  async scheduleDraftPush(restaurantId: number, area: SyncDraftArea, pushAt: string): Promise<SyncDraftStatus> {
    const response = await this.client.put<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}/schedule`,
      { pushAt }
    );
    return response.data;
  }

  /** Removes a scheduled push, leaving the area staged. */
  async clearDraftSchedule(restaurantId: number, area: SyncDraftArea): Promise<SyncDraftStatus> {
    const response = await this.client.delete<SyncDraftStatus>(
      `/v1/restaurants/${restaurantId}/sync/draft/${area}/schedule`
    );
    return response.data;
  }

  // ==================== Floor API ====================

  async getFloors(restaurantId: number): Promise<FloorInfo[]> {
    const response = await this.client.get<FloorInfo[]>(`/v1/restaurants/${restaurantId}/floors`);
    return response.data;
  }

  async getFloor(restaurantId: number, floorId: number): Promise<FloorInfo> {
    const response = await this.client.get<FloorInfo>(`/v1/restaurants/${restaurantId}/floors/${floorId}`);
    return response.data;
  }

  async createFloor(restaurantId: number, data: Partial<FloorInfo>): Promise<FloorInfo> {
    const response = await this.client.post<FloorInfo>(`/v1/restaurants/${restaurantId}/floors`, data);
    return response.data;
  }

  async updateFloor(restaurantId: number, floorId: number, data: Partial<FloorInfo>): Promise<FloorInfo> {
    const response = await this.client.put<FloorInfo>(`/v1/restaurants/${restaurantId}/floors/${floorId}`, data);
    return response.data;
  }

  async deleteFloor(restaurantId: number, floorId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/floors/${floorId}`);
  }

  async reorderFloors(restaurantId: number, ids: number[]): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/floors/reorder`, { ids });
  }

  async uploadFloorImage(restaurantId: number, floorId: number, file: File): Promise<FloorInfo> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<FloorInfo>(
      `/v1/restaurants/${restaurantId}/floors/${floorId}/image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async deleteFloorImage(restaurantId: number, floorId: number): Promise<FloorInfo> {
    const response = await this.client.delete<FloorInfo>(
      `/v1/restaurants/${restaurantId}/floors/${floorId}/image`
    );
    return response.data;
  }

  // ==================== Managed Table API ====================

  async getManagedTables(restaurantId: number): Promise<ManagedTableInfo[]> {
    const response = await this.client.get<ManagedTableInfo[]>(`/v1/restaurants/${restaurantId}/managed-tables`);
    return response.data;
  }

  async getManagedTablesByFloor(restaurantId: number, floorId: number): Promise<ManagedTableInfo[]> {
    const response = await this.client.get<ManagedTableInfo[]>(`/v1/restaurants/${restaurantId}/floors/${floorId}/tables`);
    return response.data;
  }

  async createManagedTable(restaurantId: number, floorId: number, data: Partial<ManagedTableInfo>): Promise<ManagedTableInfo> {
    const response = await this.client.post<ManagedTableInfo>(`/v1/restaurants/${restaurantId}/floors/${floorId}/tables`, data);
    return response.data;
  }

  async updateManagedTable(restaurantId: number, tableId: number, data: Partial<ManagedTableInfo>): Promise<ManagedTableInfo> {
    const response = await this.client.put<ManagedTableInfo>(`/v1/restaurants/${restaurantId}/tables/${tableId}`, data);
    return response.data;
  }

  async deleteManagedTable(restaurantId: number, tableId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/tables/${tableId}`);
  }

  // Tables with no POS floor -- candidates for the layout editor's "add unassigned table" picker.
  async getUnassignedTables(restaurantId: number): Promise<TableInfo[]> {
    const response = await this.client.get<TableInfo[]>(`/v1/restaurants/${restaurantId}/tables/unassigned`);
    return response.data;
  }

  // Unplaces a table from its floor without deleting it (stays alive, unassigned).
  async detachManagedTable(restaurantId: number, tableId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/tables/${tableId}/floor-assignment`);
  }

  // ==================== Zone API ====================

  async getZones(restaurantId: number): Promise<TableZoneInfo[]> {
    const response = await this.client.get<TableZoneInfo[]>(`/v1/restaurants/${restaurantId}/zones`);
    return response.data;
  }

  async getZonesByFloor(restaurantId: number, floorId: number): Promise<TableZoneInfo[]> {
    const response = await this.client.get<TableZoneInfo[]>(`/v1/restaurants/${restaurantId}/floors/${floorId}/zones`);
    return response.data;
  }

  async createZone(restaurantId: number, floorId: number, data: Partial<TableZoneInfo>): Promise<TableZoneInfo> {
    const response = await this.client.post<TableZoneInfo>(`/v1/restaurants/${restaurantId}/floors/${floorId}/zones`, data);
    return response.data;
  }

  async updateZone(restaurantId: number, zoneId: number, data: Partial<TableZoneInfo>): Promise<TableZoneInfo> {
    const response = await this.client.put<TableZoneInfo>(`/v1/restaurants/${restaurantId}/zones/${zoneId}`, data);
    return response.data;
  }

  async deleteZone(restaurantId: number, zoneId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/zones/${zoneId}`);
  }

  async assignTablesToZone(restaurantId: number, zoneId: number, tableIds: number[]): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/zones/${zoneId}/tables`, { tableIds });
  }

  // ==================== Layout API ====================

  async getLayouts(restaurantId: number, floorId: number): Promise<FloorLayoutInfo[]> {
    const response = await this.client.get<FloorLayoutInfo[]>(`/v1/restaurants/${restaurantId}/floors/${floorId}/layouts`);
    return response.data;
  }

  async createLayout(restaurantId: number, floorId: number, data: Partial<FloorLayoutInfo>): Promise<FloorLayoutInfo> {
    const response = await this.client.post<FloorLayoutInfo>(`/v1/restaurants/${restaurantId}/floors/${floorId}/layouts`, data);
    return response.data;
  }

  async updateLayout(restaurantId: number, layoutId: number, data: { positions?: { tableId: number; x: number; y: number; width?: number; height?: number; hidden?: boolean }[]; name?: string; isDefault?: boolean; shiftId?: number }): Promise<void> {
    await this.client.put(`/v1/restaurants/${restaurantId}/layouts/${layoutId}`, data);
  }

  async deleteLayout(restaurantId: number, layoutId: number): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/layouts/${layoutId}`);
  }

  async setDefaultLayout(restaurantId: number, layoutId: number): Promise<void> {
    await this.client.post(`/v1/restaurants/${restaurantId}/layouts/${layoutId}/default`);
  }

  // ==================== Booking Rules API ====================

  async getBookingRules(restaurantId: number): Promise<BookingRuleInfo[]> {
    const response = await this.client.get<BookingRuleInfo[]>(`/v1/restaurants/${restaurantId}/booking-rules`);
    return response.data;
  }

  async createBookingRule(restaurantId: number, data: Partial<BookingRuleInfo>): Promise<BookingRuleInfo> {
    const response = await this.client.post<BookingRuleInfo>(`/v1/restaurants/${restaurantId}/booking-rules`, data);
    return response.data;
  }

  /** uuid-keyed update: claims a POS-only row into the managed table on first edit. */
  async updateBookingRule(restaurantId: number, uuid: string, data: Partial<BookingRuleInfo>): Promise<BookingRuleInfo> {
    const response = await this.client.put<BookingRuleInfo>(
      `/v1/restaurants/${restaurantId}/booking-rules/by-uuid/${encodeURIComponent(uuid)}`, data);
    return response.data;
  }

  async deleteBookingRule(restaurantId: number, uuid: string): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/booking-rules/by-uuid/${encodeURIComponent(uuid)}`);
  }

  // ==================== Booking Sections API ====================

  async getBookingSections(restaurantId: number): Promise<BookingSectionInfo[]> {
    const response = await this.client.get<BookingSectionInfo[]>(`/v1/restaurants/${restaurantId}/booking-sections`);
    return response.data;
  }

  async createBookingSection(restaurantId: number, data: Partial<BookingSectionInfo>): Promise<BookingSectionInfo> {
    const response = await this.client.post<BookingSectionInfo>(`/v1/restaurants/${restaurantId}/booking-sections`, data);
    return response.data;
  }

  async updateBookingSection(restaurantId: number, uuid: string, data: Partial<BookingSectionInfo>): Promise<BookingSectionInfo> {
    const response = await this.client.put<BookingSectionInfo>(
      `/v1/restaurants/${restaurantId}/booking-sections/by-uuid/${encodeURIComponent(uuid)}`, data);
    return response.data;
  }

  async deleteBookingSection(restaurantId: number, uuid: string): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/booking-sections/by-uuid/${encodeURIComponent(uuid)}`);
  }

  // ==================== Booking Schedules API ====================

  async getBookingSchedules(restaurantId: number): Promise<BookingScheduleInfo[]> {
    const response = await this.client.get<BookingScheduleInfo[]>(`/v1/restaurants/${restaurantId}/booking-schedules`);
    return response.data;
  }

  async createBookingSchedule(restaurantId: number, data: Partial<BookingScheduleInfo>): Promise<BookingScheduleInfo> {
    const response = await this.client.post<BookingScheduleInfo>(`/v1/restaurants/${restaurantId}/booking-schedules`, data);
    return response.data;
  }

  async updateBookingSchedule(restaurantId: number, uuid: string, data: Partial<BookingScheduleInfo>): Promise<BookingScheduleInfo> {
    const response = await this.client.put<BookingScheduleInfo>(
      `/v1/restaurants/${restaurantId}/booking-schedules/by-uuid/${encodeURIComponent(uuid)}`, data);
    return response.data;
  }

  async deleteBookingSchedule(restaurantId: number, uuid: string): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/booking-schedules/by-uuid/${encodeURIComponent(uuid)}`);
  }

  // ==================== Special Days API ====================

  async getSpecialDays(restaurantId: number): Promise<SpecialDayInfo[]> {
    const response = await this.client.get<SpecialDayInfo[]>(`/v1/restaurants/${restaurantId}/special-days`);
    return response.data;
  }

  async createSpecialDay(restaurantId: number, data: Partial<SpecialDayInfo>): Promise<SpecialDayInfo> {
    const response = await this.client.post<SpecialDayInfo>(`/v1/restaurants/${restaurantId}/special-days`, data);
    return response.data;
  }

  async updateSpecialDay(restaurantId: number, uuid: string, data: Partial<SpecialDayInfo>): Promise<SpecialDayInfo> {
    const response = await this.client.put<SpecialDayInfo>(
      `/v1/restaurants/${restaurantId}/special-days/by-uuid/${encodeURIComponent(uuid)}`, data);
    return response.data;
  }

  async deleteSpecialDay(restaurantId: number, uuid: string): Promise<void> {
    await this.client.delete(`/v1/restaurants/${restaurantId}/special-days/by-uuid/${encodeURIComponent(uuid)}`);
  }
}

// Export appropriate client based on environment
const useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';

let client: ApiClient;

if (useMockApi) {
  // Dynamically import mock client
  const { mockApiClient } = await import('./mockApi');
  client = mockApiClient as unknown as ApiClient;
} else {
  client = new ApiClient();
}

export const apiClient = client;
export default apiClient;